import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  completeResponseApiV1ReportGeneratorResponsesResponseIdCompletePut,
  createTemplateApiV1ReportGeneratorTemplatesPost,
  deleteTemplateApiV1ReportGeneratorTemplatesIdDelete,
  generateFromHtmlApiV1ReportGeneratorTemplatesIdCasesCaseNumberGenerateFromHtmlPost,
  generateReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberGenerateReportPost,
  getUserInitialAnswersApiV1ReportGeneratorTemplatesIdCasesCaseNumberGetUserInitialAnswersPost,
  previewReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberPreviewReportPost,
  refineExpertsCommentaryApiV1ReportGeneratorRefineExpertsCommentaryPost,
  renderTemplateHtmlApiV1ReportGeneratorTemplatesIdCasesCaseNumberRenderHtmlPost,
  saveDraftApiV1ReportGeneratorTemplatesIdCasesCaseNumberSaveDraftPost,
  saveReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberSaveReportPost,
  updateTemplateApiV1ReportGeneratorTemplatesIdPut,
} from "../../api/generated/sdk.gen";
import {
  CreateTemplateRequestSchema,
  GenerateReportRequestSchema,
  ReportGeneratorInitialAnswersResponseSectionSchema,
  ReportGeneratorTemplateSchema,
  UpdateTemplateRequestSchema,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { getAccessTokenCookie } from "../../utils/cookies";
import { getEnv } from "../../utils/env";

/**
 * Hook to fetch user initial answers for a specific template and case
 * @returns Mutation result for fetching user initial answers with section names as keys and section data containing modules
 */
export const useUserInitialAnswersMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (
    data: Record<string, ReportGeneratorInitialAnswersResponseSectionSchema>
  ) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
    }: {
      templateId: string;
      caseNumber: string;
      fileIds: string[];
    }) => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      if (!caseNumber) {
        throw new Error("Case number is required");
      }

      const response =
        await getUserInitialAnswersApiV1ReportGeneratorTemplatesIdCasesCaseNumberGetUserInitialAnswersPost(
          {
            client: apiClient,
            path: {
              id: templateId,
              case_number: caseNumber,
            },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch user initial answers: ${JSON.stringify(response.error)}`
        );
      }

      return response.data as Record<
        string,
        ReportGeneratorInitialAnswersResponseSectionSchema
      >;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          variables.templateId,
          variables.caseNumber,
        ],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to save draft report data for a specific template and case
 * @returns Mutation result for saving draft data with success/error handling
 */
export const useSaveDraftMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      draftData,
    }: {
      templateId?: string;
      caseNumber?: string;
      draftData: Record<string, GenerateReportRequestSchema>;
    }) => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      if (!caseNumber) {
        throw new Error("Case number is required");
      }

      const response =
        await saveDraftApiV1ReportGeneratorTemplatesIdCasesCaseNumberSaveDraftPost({
          client: apiClient,
          path: {
            id: templateId,
            case_number: caseNumber,
          },
          body: draftData,
        });

      if ("error" in response && response.error) {
        throw new Error(`Failed to save draft: ${JSON.stringify(response.error)}`);
      }

      return response.data;
    },
    onMutate: async (variables) => {
      const queryKey = [
        CLIENT_KEYS.REPORT_GENERATOR,
        CLIENT_KEYS.TEMPLATES,
        variables.templateId,
        CLIENT_KEYS.CASES,
        CLIENT_KEYS.DRAFT_STATUS,
        variables.caseNumber,
      ];

      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<{
        has_draft: boolean;
        draft_data: Record<string, GenerateReportRequestSchema>;
        [key: string]: unknown;
      }>(queryKey);

      queryClient.setQueryData(queryKey, (old: typeof previous) => ({
        has_draft_report: false,
        draft_id: null,
        created_at: null,
        updated_at: null,
        ...(old ?? {}),
        has_draft: true,
        draft_data: {
          ...(old?.draft_data ?? {}),
          ...variables.draftData,
        },
      }));

      return { previous, queryKey };
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: context!.queryKey });
      onSuccess?.(data);
    },
    onError: (error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      onError?.(error);
    },
  });
};

/**
 * Hook to generate a report for a specific template and case
 * @returns Mutation result for generating a report with success/error handling
 */
export const useGenerateReportMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      reportData,
      file,
    }: {
      templateId: string;
      caseNumber: string;
      reportData: Record<string, GenerateReportRequestSchema>;
      file: File | Blob;
    }) => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      if (!caseNumber) {
        throw new Error("Case number is required");
      }

      if (!file) {
        throw new Error("File is required");
      }

      const response =
        await generateReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberGenerateReportPost(
          {
            client: apiClient,
            path: {
              id: templateId,
              case_number: caseNumber,
            },
            body: {
              file,
              req_body: JSON.stringify(reportData),
            },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to generate report: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate any relevant queries
      queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          variables.templateId,
          CLIENT_KEYS.CASES,
          variables.caseNumber,
        ],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to preview a report for a specific template and case
 * @returns Mutation result for generating a report preview with success/error handling
 */
export const usePreviewReportMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
} = {}) => {
  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      reportData,
    }: {
      templateId: string;
      caseNumber: string;
      reportData: Record<string, GenerateReportRequestSchema>;
    }) => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      if (!caseNumber) {
        throw new Error("Case number is required");
      }

      const response =
        await previewReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberPreviewReportPost(
          {
            client: apiClient,
            path: {
              id: templateId,
              case_number: caseNumber,
            },
            body: reportData,
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to preview report: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data, _variables) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to generate a final DOCX from a single HTML body composed by the
 * Review-and-Download page.
 */
/**
 * Render the case's template DOCX as HTML for the Tiptap-based Review page.
 * The backend auto-fills Group A placeholders (doctor name, client name,
 * date_of_examination) and optionally splices accepted section content
 * (`body_html`) between the Thank-You and Yours-Sincerely anchors.
 */
export const useRenderTemplateHtmlMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: { html: string }) => void;
  onError?: (error: Error) => void;
} = {}) => {
  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      bodyHtml,
    }: {
      templateId: string;
      caseNumber: string;
      bodyHtml?: string;
    }) => {
      if (!templateId) throw new Error("Template ID is required");
      if (!caseNumber) throw new Error("Case number is required");

      const response =
        await renderTemplateHtmlApiV1ReportGeneratorTemplatesIdCasesCaseNumberRenderHtmlPost(
          {
            client: apiClient,
            path: { id: templateId, case_number: caseNumber },
            body: { body_html: bodyHtml ?? null },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to render template HTML: ${JSON.stringify(response.error)}`
        );
      }

      return response.data as { html: string };
    },
    onSuccess: (data) => onSuccess?.(data),
    onError: (error) => onError?.(error),
  });
};

export const useGenerateFromHtmlMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: { download_url?: string }) => void;
  onError?: (error: Error) => void;
} = {}) => {
  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      html,
    }: {
      templateId: string;
      caseNumber: string;
      html: string;
    }) => {
      if (!templateId) throw new Error("Template ID is required");
      if (!caseNumber) throw new Error("Case number is required");

      const response =
        await generateFromHtmlApiV1ReportGeneratorTemplatesIdCasesCaseNumberGenerateFromHtmlPost(
          {
            client: apiClient,
            path: { id: templateId, case_number: caseNumber },
            body: { html },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to generate report: ${JSON.stringify(response.error)}`
        );
      }

      return response.data as { download_url?: string };
    },
    onSuccess: (data) => onSuccess?.(data),
    onError: (error) => onError?.(error),
  });
};

/**
 * Hook to refine experts commentary text
 * @returns Mutation result for refining experts commentary
 */
export const useRefineExpertsCommentaryMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
} = {}) => {
  return useMutation({
    mutationFn: async ({ originalText }: { originalText: string }) => {
      if (!originalText?.trim()) {
        throw new Error("Original text is required");
      }

      const response =
        await refineExpertsCommentaryApiV1ReportGeneratorRefineExpertsCommentaryPost(
          {
            client: apiClient,
            body: {
              original_text: originalText,
            },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to refine experts commentary: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to complete a response (mark as COMPLETED to start fresh)
 * @returns Mutation result for completing a response
 */
export const useCompleteResponseMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ responseId }: { responseId: string }) => {
      if (!responseId) {
        throw new Error("Response ID is required");
      }

      const response =
        await completeResponseApiV1ReportGeneratorResponsesResponseIdCompletePut({
          client: apiClient,
          path: {
            response_id: responseId,
          },
        });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to complete response: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: (data, _variables) => {
      // Invalidate draft-related queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.RESPONSES],
      });
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.TEMPLATES],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to save report (update existing draft file and mark as COMPLETED)
 * @returns Mutation result for saving a report with success/error handling
 */
export const useSaveReportMutation = ({
  onSuccess,
  onError,
  skipInvalidation = false,
}: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
  skipInvalidation?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      file,
    }: {
      templateId: string;
      caseNumber: string;
      file: File | Blob;
    }) => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      if (!caseNumber) {
        throw new Error("Case number is required");
      }

      if (!file) {
        throw new Error("File is required");
      }

      const response =
        await saveReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberSaveReportPost(
          {
            client: apiClient,
            path: {
              id: templateId,
              case_number: caseNumber,
            },
            body: {
              file,
            },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(`Failed to save report: ${JSON.stringify(response.error)}`);
      }

      return response.data;
    },
    onSuccess: (data, _variables) => {
      // Only invalidate queries if not skipping invalidation (e.g., for auto-save)
      if (!skipInvalidation) {
        // Invalidate draft-related queries
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.TEMPLATES],
        });
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.RESPONSES],
        });
      }
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to create a new template
 * @returns Mutation result for creating a template with success/error handling
 */
export const useCreateTemplate = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ReportGeneratorTemplateSchema) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: CreateTemplateRequestSchema) => {
      const response = await createTemplateApiV1ReportGeneratorTemplatesPost({
        client: apiClient,
        body: templateData,
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to create template: ${JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        throw new Error("No data returned from template creation");
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.TEMPLATES],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to update an existing template
 * @returns Mutation result for updating a template with success/error handling
 */
export const useUpdateTemplate = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ReportGeneratorTemplateSchema) => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      templateData,
    }: {
      templateId: string;
      templateData: UpdateTemplateRequestSchema;
    }) => {
      const response = await updateTemplateApiV1ReportGeneratorTemplatesIdPut({
        client: apiClient,
        path: { id: templateId },
        body: templateData,
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to update template: ${JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        throw new Error("No data returned from template update");
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.TEMPLATES],
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Hook to delete an existing template
 * @returns Mutation result for deleting a template with success/error handling
 */
export const useDeleteTemplate = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const response = await deleteTemplateApiV1ReportGeneratorTemplatesIdDelete({
        client: apiClient,
        path: { id: templateId },
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to delete template: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.TEMPLATES],
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/**
 * Persist the editor's current .docx as a WIP draft.
 *
 * Called on the SplitDocx auto-save tick — uploads the ArrayBuffer
 * from `editorRef.current.save()` as multipart/form-data so the
 * backend can stamp `draft_docx_url` + `draft_docx_saved_at` on the
 * draft response (creating it if it doesn't exist yet).
 *
 * Uses raw apiClient.put because the SDK generator hasn't been re-
 * run against the new endpoint yet; safe to migrate to the
 * generated method once `update-api-client.sh` is run.
 */
export type UploadDraftDocxResult = {
  draft_docx_url: string;
  draft_docx_saved_at: string;
};

export const useUploadDraftDocxMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      caseNumber,
      docx,
    }: {
      templateId: string;
      caseNumber: string;
      docx: ArrayBuffer | Blob;
    }): Promise<UploadDraftDocxResult> => {
      const form = new FormData();
      const blob =
        docx instanceof Blob
          ? docx
          : new Blob([docx], {
              type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
      form.append("file", blob, "draft.docx");

      // Use raw fetch (not apiClient) so the browser sets the multipart
      // Content-Type + boundary itself. Routing FormData through the hey-api
      // client mangled the multipart body → backend 422 (missing `file`).
      const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
      const url = `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator/templates/${encodeURIComponent(
        templateId
      )}/cases/${encodeURIComponent(caseNumber)}/draft-docx`;
      const token = getAccessTokenCookie();
      const resp = await fetch(url, {
        method: "PUT",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!resp.ok) {
        const detail = await resp.text().catch(() => "");
        throw new Error(
          `Failed to save draft .docx (${resp.status}): ${detail || resp.statusText}`
        );
      }
      return (await resp.json()) as UploadDraftDocxResult;
    },
    onSuccess: (_data, variables) => {
      // Bust any cached `getResponseById` so the next read sees the
      // fresh draft URL + timestamp.
      queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.REPORT_GENERATOR,
          CLIENT_KEYS.TEMPLATES,
          variables.templateId,
          CLIENT_KEYS.CASES,
          CLIENT_KEYS.DRAFT_STATUS,
          variables.caseNumber,
        ],
      });
    },
  });
};

/**
 * Download a response's generated report as PDF.
 *
 * Backend (#587): POST /responses/{response_id}/download-pdf converts the
 * generated .docx → PDF on demand (LibreOffice) and streams it back. POST
 * (not GET) so an optional `password` for PDF encryption rides in the body,
 * not the URL. We fetch the binary with the bearer token and trigger a
 * browser download. `response_id` is the draft response id (draftStatus.draft_id).
 */
export const useDownloadResponsePdf = () => {
  return useMutation({
    mutationFn: async ({
      responseId,
      fileName,
      password,
    }: {
      responseId: string;
      fileName?: string;
      password?: string;
    }): Promise<void> => {
      const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
      const url = `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator/responses/${encodeURIComponent(
        responseId
      )}/download-pdf`;
      const token = getAccessTokenCookie();
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(password ? { password } : {}),
      });
      if (!resp.ok) {
        const detail = await resp.text().catch(() => "");
        throw new Error(
          `Failed to download PDF (${resp.status}): ${detail || resp.statusText}`
        );
      }
      const blob = await resp.blob();
      // Force a PDF type so the browser treats it as a download, not a
      // navigation/inline view, even if the response Content-Type is off.
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName ?? "report.pdf";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke — revoking synchronously after click can cancel the
      // download in some browsers before it starts.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    },
  });
};

/**
 * Download the server-merged .docx for a draft response.
 *
 * The in-editor Download button used to call `editor.save()` directly,
 * which strips header/footer media because the @eigenpal docx editor's
 * parser doesn't traverse anchored textboxes. This endpoint returns the
 * merged blob held on the server — same one the PDF endpoint converts —
 * so the .docx the user downloads carries the letterhead.
 */
export const useDownloadResponseDocx = () => {
  return useMutation({
    mutationFn: async ({
      responseId,
      fileName,
    }: {
      responseId: string;
      fileName?: string;
    }): Promise<void> => {
      const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
      const url = `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator/responses/${encodeURIComponent(
        responseId
      )}/download-docx`;
      const token = getAccessTokenCookie();
      const resp = await fetch(url, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const detail = await resp.text().catch(() => "");
        throw new Error(
          `Failed to download .docx (${resp.status}): ${detail || resp.statusText}`
        );
      }
      const blob = await resp.blob();
      const docxType =
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const docxBlob =
        blob.type === docxType ? blob : new Blob([blob], { type: docxType });
      const objectUrl = URL.createObjectURL(docxBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName ?? "report.docx";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    },
  });
};
